import { useState, type KeyboardEvent } from "react";
import { PsvReviewStatus } from "@api/shared-runtime/psv-review";
import type { ReportItem, RunReport, RunScope } from "../types";
import {
  isExpandedByDefault,
  isOpenItem,
  isOpenValidItem,
} from "./item-status";
import { useItemDecisions } from "./use-item-decisions";

const KEY = { Next: "j", Previous: "k", Accept: "a", Concern: "c" } as const;
const TYPING_TARGETS = "input, textarea, select, [contenteditable='true']";

/** Everything the report's items share: decisions, which rows are expanded,
 * the accept-without-evidence confirmation and the keyboard shortcuts. */
export function useReportReview({
  data,
  scope,
  canEdit,
}: {
  data: RunReport | undefined;
  scope: RunScope;
  canEdit: boolean;
}) {
  const items = data?.items ?? [];
  const decisions = useItemDecisions({ scope, runId: data?.run.id ?? "" });
  const [expansion, setExpansion] = useState<Record<string, boolean>>({});
  const [confirmItem, setConfirmItem] = useState<ReportItem | null>(null);
  const [reopenItem, setReopenItem] = useState<ReportItem | null>(null);
  const [uploadFocusKey, setUploadFocusKey] = useState<string | null>(null);
  const validOpenItems = items.filter(isOpenValidItem);

  function isExpanded(item: ReportItem) {
    return expansion[item.key] ?? isExpandedByDefault(item);
  }

  function toggle(item: ReportItem) {
    setExpansion((current) => ({
      ...current,
      [item.key]: !(current[item.key] ?? isExpandedByDefault(item)),
    }));
  }

  function expand(item: ReportItem) {
    setExpansion((current) => ({ ...current, [item.key]: true }));
  }

  /** A decided item collapses back to its default. */
  function decide({
    item,
    status,
  }: {
    item: ReportItem;
    status: PsvReviewStatus;
  }) {
    setExpansion((current) => {
      const next = { ...current };
      delete next[item.key];
      return next;
    });
    void decisions.setStatus({ item, status });
  }

  function accept(item: ReportItem) {
    if (!item.hasEvidence) {
      setConfirmItem(item);
      return;
    }
    decide({ item, status: PsvReviewStatus.Accepted });
  }

  function concern(item: ReportItem) {
    decide({ item, status: PsvReviewStatus.Concern });
  }

  /** A carried decision needs a reason, because reopening stops later runs
   * from carrying it forward. */
  function undo(item: ReportItem) {
    if (item.decision?.carriedFrom) {
      setReopenItem(item);
      return;
    }
    void decisions.setStatus({
      item,
      status: PsvReviewStatus.Pending,
      offerUndo: false,
    });
  }

  function reopen(reason: string) {
    if (reopenItem) {
      void decisions.setStatus({
        item: reopenItem,
        status: PsvReviewStatus.Pending,
        offerUndo: false,
        reason,
      });
    }
    setReopenItem(null);
  }

  function acceptAnyway() {
    if (confirmItem) {
      decide({ item: confirmItem, status: PsvReviewStatus.Accepted });
    }
    setConfirmItem(null);
  }

  function requestUpload() {
    if (confirmItem) {
      setExpansion((current) => ({ ...current, [confirmItem.key]: true }));
      setUploadFocusKey(confirmItem.key);
    }
    setConfirmItem(null);
  }

  function acceptAllValid() {
    void decisions.acceptAllValid(validOpenItems);
  }

  function moveFocus({
    root,
    from,
    step,
  }: {
    root: HTMLElement;
    from: HTMLElement | null;
    step: number;
  }) {
    const toggles = Array.from(
      root.querySelectorAll<HTMLElement>("[data-item-toggle]"),
    );
    const current = toggles.findIndex(
      (button) => button === from?.querySelector("[data-item-toggle]"),
    );
    const next = Math.min(Math.max(current + step, 0), toggles.length - 1);
    toggles[next]?.focus();
  }

  /** Scoped to the report. Events from portals (the confirmation, evidence
   * previews) and from text fields are left alone. */
  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    const target = event.target;
    if (
      !(target instanceof HTMLElement) ||
      !event.currentTarget.contains(target) ||
      target.closest(TYPING_TARGETS) ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey
    ) {
      return;
    }
    const key = event.key.toLowerCase();
    const row = target.closest<HTMLElement>("[data-item-key]");
    if (key === KEY.Next || key === KEY.Previous) {
      event.preventDefault();
      moveFocus({
        root: event.currentTarget,
        from: row,
        step: key === KEY.Next ? 1 : -1,
      });
      return;
    }
    const item = items.find((entry) => entry.key === row?.dataset.itemKey);
    if (!item || !canEdit || !isOpenItem(item)) {
      return;
    }
    if (key === KEY.Accept) {
      accept(item);
    } else if (key === KEY.Concern) {
      concern(item);
    }
  }

  return {
    items,
    decisions,
    validOpenItems,
    confirmItem,
    reopenItem,
    uploadFocusKey,
    isExpanded,
    toggle,
    expand,
    accept,
    concern,
    undo,
    reopen,
    cancelReopen: () => setReopenItem(null),
    acceptAnyway,
    requestUpload,
    cancelConfirm: () => setConfirmItem(null),
    clearUploadFocus: () => setUploadFocusKey(null),
    acceptAllValid,
    onKeyDown,
  };
}

export type ReportReview = ReturnType<typeof useReportReview>;
